// Shared, deduplicated fetcher for GET /api/auth/me.
//
// On a cold page load three independent consumers need /me within the
// same tick: useAuth's probe, the workspace list adapter, and the
// workspace switcher. Without sharing, each fired its own request
// (observed: 3x /api/auth/me per refresh). One in-flight promise is
// shared and the result is cached briefly; auth transitions must call
// bustMeCache() so a stale identity never outlives a login/logout.

export interface MeResponse {
  user: {
    id: string;
    email: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  workspaces: {
    id: string;
    name: string;
    owner_id: string;
    created_at: number;
  }[];
}

const TTL_MS = 5_000;

let generation=0;
export class MeRequestError extends Error{constructor(public status:number){super(`Unable to check your session (${status}).`);}}
let _cache: { at: number; value: MeResponse } | null = null;
let _inflight: Promise<MeResponse> | null = null;

export function bustMeCache(): void {
  generation++;
  _cache = null;
  _inflight = null;
}

/** Fetch /api/auth/me once per TTL window; concurrent callers share one request. Throws on non-OK. */
export async function fetchMeShared(): Promise<MeResponse> {
  if (_cache && Date.now() - _cache.at < TTL_MS) return _cache.value;
  if (_inflight) return _inflight;
  const version=generation;
  _inflight = (async () => {
    try {
      const res = await fetch('/api/auth/me', { credentials: 'include' });
      if (!res.ok) throw new MeRequestError(res.status);
      const value = (await res.json()) as MeResponse;
      if(version===generation)_cache = { at: Date.now(), value };
      return value;
    } finally {
      if(version===generation)_inflight = null;
    }
  })();
  return _inflight;
}
